import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table", "searchInput", "columnInput", "rowsPerPage", "info", "tbody"]

  connect() {
    if (!this.hasTbodyTarget) {
      console.warn("DataTable: tbody target not found")
      return
    }
    
    this.originalRows = Array.from(this.tbodyTarget.querySelectorAll("tr"))
    this.currentRows = [...this.originalRows]
    this.currentPage = 1
    this.rowsPerPage = this.hasRowsPerPageTarget ? parseInt(this.rowsPerPageTarget.value) : 10
    this.sortColumn = null
    this.sortDirection = "asc"
    this.updateDisplay()
  }

  search(event) {
    if (!this.hasTbodyTarget || !this.originalRows) return
    
    const query = event.target.value.toLowerCase().trim()
    
    if (query === "") {
      this.currentRows = [...this.originalRows]
    } else {
      this.currentRows = this.originalRows.filter(row => {
        const text = row.textContent.toLowerCase()
        return text.includes(query)
      })
    }
    
    this.currentPage = 1
    this.updateDisplay()
  }

  searchColumn(event) {
    if (!this.hasTbodyTarget || !this.originalRows) return
    
    const columnIndex = parseInt(event.target.dataset.column)
    const query = event.target.value.toLowerCase().trim()
    
    // Get all column inputs and their values
    const columnFilters = {}
    this.columnInputTargets.forEach(input => {
      const col = parseInt(input.dataset.column)
      const val = input.value.toLowerCase().trim()
      if (val) {
        columnFilters[col] = val
      }
    })
    
    // Filter rows based on all column filters
    if (Object.keys(columnFilters).length === 0) {
      this.currentRows = [...this.originalRows]
    } else {
      this.currentRows = this.originalRows.filter(row => {
        const cells = row.querySelectorAll("td")
        return Object.entries(columnFilters).every(([col, val]) => {
          const cell = cells[parseInt(col)]
          return cell && cell.textContent.toLowerCase().includes(val)
        })
      })
    }
    
    this.currentPage = 1
    this.updateDisplay()
  }

  sortAsc(event) {
    if (!this.hasTbodyTarget || !this.originalRows) return
    const column = parseInt(event.currentTarget.dataset.column)
    this.sort(column, "asc")
  }

  sortDesc(event) {
    if (!this.hasTbodyTarget || !this.originalRows) return
    const column = parseInt(event.currentTarget.dataset.column)
    this.sort(column, "desc")
  }

  sort(columnIndex, direction) {
    if (!this.currentRows) return
    
    this.sortColumn = columnIndex
    this.sortDirection = direction
    
    // Update sort indicator styling
    this.updateSortIndicators(columnIndex, direction)
    
    this.currentRows.sort((a, b) => {
      const cellA = a.querySelectorAll("td")[columnIndex]?.textContent.trim() || ""
      const cellB = b.querySelectorAll("td")[columnIndex]?.textContent.trim() || ""
      
      // Try to parse as number
      const numA = parseFloat(cellA.replace(/[^0-9.-]/g, ""))
      const numB = parseFloat(cellB.replace(/[^0-9.-]/g, ""))
      
      let comparison = 0
      if (!isNaN(numA) && !isNaN(numB)) {
        comparison = numA - numB
      } else {
        comparison = cellA.localeCompare(cellB)
      }
      
      return direction === "asc" ? comparison : -comparison
    })
    
    this.updateDisplay()
  }

  updateSortIndicators(columnIndex, direction) {
    // Remove active class from all sort arrows
    this.element.querySelectorAll("[data-action*='sortAsc'], [data-action*='sortDesc']").forEach(arrow => {
      arrow.classList.remove("text-primary", "bg-primary", "rounded")
      arrow.querySelector("svg")?.classList.remove("fill-primary")
    })
    
    // Find and highlight the active sort arrow
    const selector = direction === "asc" 
      ? `[data-action*="sortAsc"][data-column="${columnIndex}"]`
      : `[data-action*="sortDesc"][data-column="${columnIndex}"]`
    
    const activeArrow = this.element.querySelector(selector)
    if (activeArrow) {
      activeArrow.classList.add("text-primary")
      const svg = activeArrow.querySelector("svg")
      if (svg) {
        svg.classList.add("fill-primary")
      }
    }
  }

  changeRowsPerPage(event) {
    if (!this.hasTbodyTarget || !this.originalRows) return
    
    const value = event.target.value
    this.rowsPerPage = value === "-1" ? this.originalRows.length : parseInt(value)
    this.currentPage = 1
    this.updateDisplay()
  }

  updateDisplay() {
    if (!this.hasTbodyTarget) return
    
    // Clear tbody
    this.tbodyTarget.innerHTML = ""
    
    // Calculate pagination
    const start = (this.currentPage - 1) * this.rowsPerPage
    const end = start + this.rowsPerPage
    const visibleRows = this.currentRows.slice(start, end)
    
    // Append visible rows
    visibleRows.forEach(row => {
      this.tbodyTarget.appendChild(row.cloneNode(true))
    })
    
    // Update info
    if (this.hasInfoTarget) {
      const showing = this.currentRows.length > 0 ? start + 1 : 0
      const to = Math.min(end, this.currentRows.length)
      this.infoTarget.textContent = `Showing ${showing} to ${to} of ${this.currentRows.length} entries`
    }
  }
}
